import React, { useEffect, useMemo, useState } from 'react';
import { svgIcons } from '@/components/UI/SvgIcons';
import Layout from '@/components/Layout/Layout';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import PageHeading from '@/components/common/PageHeading';
import SyncProductModel from '@/components/common/Products/SyncProductModel';
import Model from '@/components/common/models/Model';
import UploadField from '@/components/UI/UploadField';
import Card from '@/components/UI/Card';
import CommonFilter from '@/components/common/CommonFilter';
import {
  checkIsExpire,
  GetFilterUrl,
  getStatusWarning,
  productOptions,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import ExportOption from '@/components/common/export/ExportOption';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import ImagePreview from '@/components/common/media/ImagePreview';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { RoutesLink } from '@/utility/RouteLinks';
import { usePermissions } from '@/hooks/usePermissions';
import { Star } from 'lucide-react';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import { getPermissionName } from '@/context/permission';

const { RefreshIcon } = svgIcons;

interface ProductStates {
  isLoading: boolean;
  uploadModel: boolean;
  productStatus: string;
  products: IproductResponse[];
  pageInfo: PageInfo;
  selectedProductIds: Set<string | number>;
  showDeleteModel: boolean;
  selectedProductId: string | null;
  showUpdateModel: boolean;
  statusToUpdate: string | null;
  orderDir: string;
}

const statusOptions = [
  ...productOptions,
  {
    value: 'out_of_stock',
    label: 'Out of stock',
  },
];

const Products: React.FC = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const { canView, canEdit, canDelete } = usePermissions();

  const [states, setStates] = useState<ProductStates>({
    isLoading: false,
    productStatus: 'active',
    uploadModel: false,
    products: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    orderDir: '',
    showDeleteModel: false,
    showUpdateModel: false,
    statusToUpdate: null,
    selectedProductIds: new Set(),
    selectedProductId: null,
  });
  const [model, setModel] = useState<boolean>(false);

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const onStatusUpdate = async (id: string, status: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${id}`;
      await callApi(PATH, { method: 'PATCH', data: { status } });
      fetchProducts();
    } catch (error) {
      console.log('error', error);
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const fetchProducts = async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.productStatus,
        search: debounceValue,
        sort: states.orderDir,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? `${query}` : ''}`;
      const res = await callApi<IproductResponseList>(PATH, { method: 'GET' });
      handleChangeState({
        products: res.data,
        pageInfo: {
          limit: res.pageInfo.limit,
          page: res.pageInfo.page,
          total: res.pageInfo.total,
          totalPages: res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
      console.log('error is', error);
    }
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.products);

  const handleDeleteProduct = async (id: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
      handleChangeState({
        pageInfo: {
          ...states.pageInfo,
          page: 1,
        },
      });
      fetchProducts();
      showToastNotification('success', 'Product deleted successfully');
    } catch (error: unknown) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error', error);
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.productStatus,
    debounceValue,
    states.orderDir,
  ]);

  // SYNC API CALL FUNCATION
  const syncProducts = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.sync}/products`;
      await callApi(PATH, { method: 'GET' }); // SYNC API CALL
      setModel(!model);
      handleChangeState({ isLoading: false });
    } catch (error) {
      handleChangeState({ isLoading: false });
      console.log('error is', error);
    }
  };

  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => {
        const url = item?.directImageUrl
          ? item?.directImageUrl
          : item?.media?.[0]?.url;
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${url}`}
            alt={''}
            text={item.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={productOptions}
          onChange={(status) => {
            handleChangeState({
              showUpdateModel: true,
              selectedProductId: item.id,
              statusToUpdate: status,
            });
          }}
          canEditVal="product"
        />
      ),
    },
    {
      key: 'stock',
      header: 'Unit Stock',
      render: (item) => item.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'stock',
      header: 'CTN Stock',
      render: (item) => item.productInfo[0]?.primaryQuantity || 0,
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) => (
        <span>
          {checkIsExpire(item?.productInfo[0]?.discountEndDate) ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'ItemCode',
      header: 'Item Code',
      render: (item) => <span>{item?.itemCode || '--'}</span>,
    },
    {
      key: 'ItemId',
      header: 'Item Id',
      render: (item) => <span>{item?.inventoryItemId}</span>,
    },
    {
      key: 'averageRating',
      header: 'Rating',
      render: (item) => (
        <div className="p-3 flex items-center gap-1">
          {toFixedThree(item?.averageRating || 0, 1)}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  const productActionConfig: Record<string, ActionConfig<IproductResponse>> = {
    view: {
      text: 'View',
      onClick: (product) =>
        navigate(`${RoutesLink.productDetail}/${product.id}`),
      permission: () => canView('product'),
    },
    eidt: {
      text: 'Edit',
      onClick: (product) => navigate(`${RoutesLink.addProducts}/${product.id}`),
      permission: () => canEdit('product'),
    },
    delete: {
      text: 'Delete',
      onClick: (product) => {
        handleChangeState({
          showDeleteModel: true,
          selectedProductId: product.id,
        });
      },
      permission: () => canDelete('product'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(productActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit, canDelete],
  );

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  const filterOption = [
    { value: 'az', label: 'A to Z' },
    { value: 'za', label: 'Z to A' },
  ];

  return (
    <Layout>
      <div className="flex items-center gap-3 justify-between">
        <PageHeading
          text="Products"
          button={
            canEdit(getPermissionName('update:product'))
              ? {
                  icon: RefreshIcon,
                  text: 'Sync Products',
                  onClick: () => setModel(!model),
                }
              : undefined
          }
        />
        {canEdit(getPermissionName('add:product')) && (
          <button
            type="button"
            className="mb-6 btn-secondary"
            onClick={() => handleChangeState({ uploadModel: true })}
          >
            Upload Images
          </button>
        )}
      </div>
      <SyncProductModel
        onClose={() => setModel(!model)}
        show={model}
        isLoading={states.isLoading}
        onSubmit={syncProducts}
      />
      <Model
        show={states.uploadModel}
        title="Select the file/folder"
        size="w-2xl"
        onClose={() => handleChangeState({ uploadModel: false })}
      >
        <UploadField
          referenceType="product"
          multiple={true}
          helperText="You can select a folder (up to 40 files) or select files individually."
          showPreviews={false}
          folderUpload
          onChangeMedia={() => handleChangeState({ uploadModel: false })}
        />
      </Model>
      <Card>
        <div className="flex items-center justify-between gap-3">
          <CommonFilter
            filters={statusOptions}
            inputPlaceHolder="Search for products..."
            onChange={(filter) => {
              handleChangeState({
                productStatus: filter,
                pageInfo: { ...states.pageInfo, page: 1 },
              });
            }}
            filterOptions={{
              values: filterOption,
              onChange: (value) => handleChangeState({ orderDir: value }),
            }}
            onSearch={(text) => setSearchValue(text)}
          />
          {states.products.length > 0 && (
            <ExportOption
              APIPath={`${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/export/excel`}
              status={states.productStatus}
            />
          )}
        </div>
        <CommonTable
          columns={columns}
          data={states.products}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No products found"
          loading={states.isLoading}
          actions={actions}
          minHeight="min-h-44"
        />
        <Pagination
          isLoading={states.isLoading}
          paginationData={states.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>
      {states.showDeleteModel && (
        <ConfirmationModel
          show={true}
          onClose={() => {
            handleChangeState({
              showDeleteModel: false,
              selectedProductId: null,
            });
          }}
          type="product"
          onConfirm={() =>
            handleDeleteProduct(states.selectedProductId as string)
          }
        />
      )}
      {states.showUpdateModel && (
        <ConfirmationModel
          modelType="update"
          show={states.showUpdateModel}
          warning={
            states.statusToUpdate === 'inactive'
              ? getStatusWarning('product')
              : ''
          }
          onClose={() => {
            handleChangeState({
              showUpdateModel: false,
              selectedProductId: null,
            });
          }}
          subTitle={`This action will update the status to ${states.statusToUpdate}`}
          onConfirm={() =>
            onStatusUpdate(
              states.selectedProductId as string,
              states.statusToUpdate as string,
            )
          }
        />
      )}
    </Layout>
  );
};

export default Products;
